<template>
  <div class="min-h-screen flex flex-col items-center justify-center p-5 bg-primary30">
    <div class="mb-12">
      <Logo width="160px" logo="full_text_white" />
    </div>

    <div class="w-full flex justify-center">
      <div
        class="w-full max-w-md bg-white rounded shadow-lg p-10 flex flex-col items-center text-center"
      >
        <h1 class="font-inter font-bold text-lg leading-7 text-[#333333] mb-4">
          Esqueceu sua senha?
        </h1>
        <p class="font-inter text-base text-gray-600 mb-8 max-w-xs sm:max-w-sm">
          Insira o e-mail de acesso ao painel Minha Escala e enviaremos um link de recuperação para
          você.
        </p>

        <div class="w-full mb-8" data-testid="forgot-password-email-input">
          <GenericInput
            type="email"
            label="Email"
            v-model="email"
            :isError="isEmailError"
            @validate="validateEmail"
            :rules="[rules.email]"
          />
        </div>

        <div class="w-full flex flex-col gap-4">
          <FilledButton
            text="Enviar link"
            type="submit"
            width="100%"
            :disabled="isLoading"
            :loading="isLoading"
            @click="sendResetLink"
            data-testid="forgot-password-submit-button"
          />
          <router-link to="/" class="w-full">
            <OutlineButton
              text="Voltar para o Login"
              width="100%"
              :disabled="isLoading"
              data-testid="forgot-password-back-login-button"
            />
          </router-link>
        </div>

        <div
          class="w-full mt-8 p-4 bg-[#FEF9E7] border border-[#F1C21B] rounded text-left"
          data-testid="forgot-password-doctor-app-notice"
        >
          <p class="font-inter text-sm text-textPrimary mb-3">
            <span class="font-bold">É médico(a)?</span> Recupere sua senha diretamente pelo
            aplicativo Minha Escala, na tela de login do app.
          </p>
          <AppStoreBadges class="justify-center" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useToast } from 'vue-toastification'
import * as yup from 'yup'
import Logo from '@/components/designSystem/Logo/index.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import AppStoreBadges from '@/components/designSystem/AppStoreBadges/index.vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorStatus } from '@/utils/apiError'

const toast = useToast()
const { notifyError, notifyErrorMessage } = useErrorToast()
const email = ref('')
const isEmailError = ref(false)
const isLoading = ref(false)

const ForgotPasswordSchema = yup.object({
  email: yup.string().email('Email inválido').required('Email é obrigatório')
})

const rules = reactive({
  email: () => !isEmailError.value || 'Email inválido'
})

const validateEmail = async () => {
  try {
    await ForgotPasswordSchema.validateAt('email', { email: email.value })
    isEmailError.value = false
  } catch {
    isEmailError.value = true
  }
}

/**
 * Anti-enumeração de contas: a resposta de "enviado" e a de "e-mail não
 * cadastrado" (404 do backend) mostram a MESMA mensagem neutra. Rede, timeout,
 * 5xx e limite de tentativas continuam aparecendo como erro.
 */
const NEUTRAL_SUCCESS_MESSAGE =
  'Se o e-mail estiver cadastrado, enviaremos um link para redefinir a senha.'

const validateBeforeSend = async (): Promise<boolean> => {
  try {
    await ForgotPasswordSchema.validate({ email: email.value })
    return true
  } catch (error) {
    if (!(error instanceof yup.ValidationError)) throw error
    isEmailError.value = true
    notifyErrorMessage(error.message)
    return false
  }
}

const sendResetLink = async () => {
  isEmailError.value = false
  if (!(await validateBeforeSend())) return

  isLoading.value = true
  try {
    await Network.user.forgotPasswordRequest(email.value)
    toast.success(NEUTRAL_SUCCESS_MESSAGE)
  } catch (error) {
    if (getApiErrorStatus(error) === 404) {
      toast.success(NEUTRAL_SUCCESS_MESSAGE)
    } else {
      notifyError(error, 'Não foi possível enviar o link de recuperação. Tente novamente.')
      console.error(error)
    }
  } finally {
    isLoading.value = false
  }
}
</script>
